// Intake / generic-document PDFs (jsPDF, blocks input). Report PDFs moved to the v2 engine (render/engine.js,
// reports rebuild Phase 1 P4); the markdown report path that lived here is gone.

import bidiFactory from 'bidi-js';

const _bidi = bidiFactory();

function hexToRgb(hex) {
  const h = (hex || '#0d9488').replace('#', '');
  return { r: parseInt(h.substring(0, 2), 16) || 13, g: parseInt(h.substring(2, 4), 16) || 148, b: parseInt(h.substring(4, 6), 16) || 136 };
}

let _jspdfLoaded = false;
let _hebrewFontLoaded = false;
let _hebrewFontData = null;

async function ensureJsPDF() {
  if (_jspdfLoaded || window.jspdf) { _jspdfLoaded = true; return; }
  await new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js';
    s.onload = () => { _jspdfLoaded = true; resolve(); };
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

function hasHebrew(text) {
  return /[\u0590-\u05FF]/.test(text || '');
}

async function ensureHebrewFont() {
  if (_hebrewFontLoaded) return;
  const url = 'https://cdn.jsdelivr.net/gh/google/fonts@main/ofl/notosanshebrew/NotoSansHebrew%5Bwdth%2Cwght%5D.ttf';
  try {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error('Font fetch failed: ' + resp.status);
    _hebrewFontData = await resp.arrayBuffer();
    _hebrewFontLoaded = true;
    console.log('[pdf] Hebrew font loaded:', _hebrewFontData.byteLength, 'bytes');
  } catch (e) {
    console.error('[pdf] Could not load Hebrew font:', e.message);
    try {
      const resp2 = await fetch('https://cdn.jsdelivr.net/gh/nicholasgasior/gfonts-noto-sans-hebrew@master/fonts/NotoSansHebrew-Regular.ttf');
      if (resp2.ok) { _hebrewFontData = await resp2.arrayBuffer(); _hebrewFontLoaded = true; }
    } catch (e2) { console.error('[pdf] Fallback font also failed'); }
  }
  // No separate bold TTF: the static NotoSansHebrew weights (Regular/Medium/SemiBold/Bold) are
  // incompatible with jsPDF's embedder — it emits only the FIRST glyph (Hebrew bold headers rendered
  // as a single letter, e.g. "ת"). Only the variable font renders all glyphs, so we register it for
  // BOTH 'normal' and 'bold' and produce bold weight with a faux-bold stroke (fillThenStroke) at the
  // draw sites. See registerHebrewFont below and the header/subheader cases.
}

function registerHebrewFont(doc) {
  if (!_hebrewFontData) return false;
  try {
    const bytes = new Uint8Array(_hebrewFontData);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    const b64 = btoa(binary);
    doc.addFileToVFS('NotoSansHebrew-Regular.ttf', b64);
    doc.addFont('NotoSansHebrew-Regular.ttf', 'NotoSansHebrew', 'normal');
    // Register the SAME variable TTF for 'bold' — the static bold TTF is broken in jsPDF (emits only
    // the first glyph). Bold weight for Hebrew is produced by a faux-bold stroke at the draw sites,
    // not by this font, so 'bold' Hebrew still renders all glyphs correctly.
    doc.addFont('NotoSansHebrew-Regular.ttf', 'NotoSansHebrew', 'bold');
    return true;
  } catch (e) {
    console.error('[pdf] Font registration failed:', e);
    return false;
  }
}

// Reorder a string to visual order via the Unicode Bidi Algorithm (UAX#9), with
// paired-bracket mirroring. Base direction is auto (first-strong). No-op for strings
// without Hebrew \u2014 returns the same reference, so pure-LTR output is unchanged.
// Replaces the old whole-line reversal, which mangled embedded LTR runs (Latin names,
// numbers, ISO dates) and never mirrored brackets.
function bidiReorder(text) {
  if (!hasHebrew(text)) return text;
  const embeddingLevels = _bidi.getEmbeddingLevels(text);
  return _bidi.getReorderedString(text, embeddingLevels);
}

// Load logo from Supabase storage as base64 data URL
async function loadLogoImage(logoPath, bucket) {
  if (!logoPath) return null;
  try {
    const _supa = window.HUD?._supa;
    if (!_supa) { console.warn('[pdf] No Supabase client for logo'); return null; }
    const { data: urlData, error } = await _supa.storage.from(bucket || 'specialist-storage').createSignedUrl(logoPath, 60);
    if (error || !urlData?.signedUrl) { console.error('[pdf] Logo signed URL error:', error); return null; }
    const resp = await fetch(urlData.signedUrl);
    if (!resp.ok) { console.error('[pdf] Logo fetch failed:', resp.status); return null; }
    const blob = await resp.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => { console.error('[pdf] Logo read error'); resolve(null); };
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.error('[pdf] Logo load error:', e);
    return null;
  }
}

// Blocks-array entry only (intake/generic docs). A string (the old markdown report path) is refused: report PDFs
// are made by the v2 engine from the report's Tiptap content (render/engine.js).
export async function generatePDFBlob(blocks, reportType, childInfo, specialistInfo, branding, opts) {
  if (!Array.isArray(blocks)) throw new Error('generatePDFBlob: report PDFs are made by the v2 engine (render/engine.js)');
  return _generateDocFromBlocks(blocks, childInfo, specialistInfo, branding, opts || {});
}

// ── Blocks-array document renderer (intake responses → PDF; mock: claude/questionnaire-pdf-mock.html) ──
// Self-contained (own doc + draw helpers) so the report renderer above stays untouched. Reuses the
// module-level font/bidi primitives. Block types: 'section' (teal heading), 'question' (muted),
// 'answer' (ink; skipped → muted em-dash), 'spacer'. opts: { dir:'rtl'|'ltr', title, meta:[{label,value}] }.
async function _generateDocFromBlocks(blocks, childInfo, specialistInfo, branding, opts) {
  await ensureJsPDF();
  const { jsPDF } = window.jspdf;
  if (!jsPDF) throw new Error('PDF library failed to load');
  const brand = branding || {};
  const rtl = opts.dir === 'rtl';
  const meta = opts.meta || [];

  // Load Hebrew font if ANY drawn text may contain Hebrew (dir, blocks, meta, title, branding).
  const anyHeb = rtl
    || blocks.some(b => hasHebrew(b && b.text))
    || meta.some(m => hasHebrew(m && m.value) || hasHebrew(m && m.label))
    || hasHebrew(opts.title) || hasHebrew(brand.practice_name);
  if (anyHeb) await ensureHebrewFont();
  let logoDataUrl = null;
  if (brand.logo_storage_path) logoDataUrl = await loadLogoImage(brand.logo_storage_path, brand.logo_bucket);

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  // Force jsPDF's own bidi engine to pass-through — we produce final visual order via bidiReorder.
  const _docText = doc.text.bind(doc);
  doc.text = (str, x, yy, o) => _docText(str, x, yy, { isInputVisual: true, isOutputVisual: true, ...(o || {}) });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginL = 20, marginR = 20, marginT = 22, marginB = 18;
  const contentW = pageW - marginL - marginR;
  let y = marginT;

  const teal = hexToRgb(brand.header_color || '#0d9488');
  const ink = { r: 26, g: 46, b: 43 }, muted = { r: 74, g: 117, b: 112 }, faint = { r: 159, g: 184, b: 180 }, rule = { r: 228, g: 236, b: 234 };
  const hebrewOK = anyHeb ? registerHebrewFont(doc) : false;
  const latinFont = 'helvetica';
  const fontFor = (str, style) => { if (hebrewOK && hasHebrew(str)) doc.setFont('NotoSansHebrew', style || 'normal'); else doc.setFont(latinFont, style || 'normal'); };
  const textAlign = rtl ? 'right' : 'left';
  const textX = rtl ? (pageW - marginR) : marginL;
  const addPage = () => { doc.addPage(); y = marginT; };
  const checkSpace = (n) => { if (y + n > pageH - marginB) addPage(); };
  const setColor = (c) => doc.setTextColor(c.r, c.g, c.b);
  // Faux-bold weight for Hebrew: jsPDF can't embed a real bold Hebrew TTF (it emits only the first
  // glyph), and the variable-regular Hebrew strokes are lighter than Helvetica at the same size — so
  // Hebrew reads faint next to Latin. Fix: draw Hebrew runs with a SAME-COLOR stroke (fillThenStroke)
  // to thicken them; HEAD_STROKE for headings/title (bold), BODY_STROKE for body (match regular
  // Helvetica). jsPDF resets Tr=0 after each stroked draw, so Latin runs stay fill-only.
  const HEAD_STROKE = 0.2, BODY_STROKE = 0.05;
  // Draw ONE line at (x,yPos). Hebrew → variable-regular + a color-matched stroke of width `sw`
  // (0 = none); Latin → latinFont at `style` (real weight). Caller sets font size first.
  const drawLine = (wl, x, yPos, color, style, align, sw) => {
    setColor(color);
    if (hebrewOK && hasHebrew(wl)) {
      doc.setFont('NotoSansHebrew', 'normal');
      if (sw > 0) { doc.setDrawColor(color.r, color.g, color.b); doc.setLineWidth(sw); doc.text(bidiReorder(wl), x, yPos, { align, renderingMode: 'fillThenStroke' }); return; }
    } else { doc.setFont(latinFont, style || 'normal'); }
    doc.text(bidiReorder(wl), x, yPos, { align });
  };
  // Wrapped multi-line draw in the doc direction, with per-line Hebrew faux-bold (sw).
  const drawWrapped = (str, size, color, style, lh, sw) => {
    doc.setFontSize(size); fontFor(str, style);
    doc.splitTextToSize(str, contentW).forEach(wl => { checkSpace(lh + 1); drawLine(wl, textX, y, color, style, textAlign, sw || 0); y += lh; });
  };

  // ── Header ──
  if (logoDataUrl) {
    try {
      const logoH = 12;
      const fmt = logoDataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
      const img = await new Promise((res, rej) => { const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = logoDataUrl; });
      const logoW = logoH * (img.naturalWidth / img.naturalHeight);
      doc.addImage(logoDataUrl, fmt, rtl ? (pageW - marginR - logoW) : marginL, y - 2, logoW, logoH);
      y += logoH + 3;
    } catch (e) { console.error('[pdf] intake logo:', e); }
  }
  if (opts.title) { doc.setFontSize(15); drawLine(opts.title, textX, y, ink, 'bold', textAlign, HEAD_STROKE); y += 8; }

  // Meta grid — two columns, label (uppercase, small, muted) above value (ink).
  if (meta.length) {
    const colW = contentW / 2;
    meta.forEach((m, i) => {
      const col = i % 2;
      if (col === 0 && i > 0) y += 10;
      checkSpace(11);
      const cx = rtl ? (pageW - marginR - col * colW) : (marginL + col * colW);
      const al = rtl ? 'right' : 'left';
      doc.setFontSize(8); drawLine(String(m.label || '').toUpperCase(), cx, y, muted, 'normal', al, BODY_STROKE);
      doc.setFontSize(10.5); drawLine(String(m.value || ''), cx, y + 4.2, ink, 'normal', al, BODY_STROKE);
    });
    y += 10;
  }
  y += 4;
  doc.setDrawColor(teal.r, teal.g, teal.b); doc.setLineWidth(0.6);
  doc.line(marginL, y, pageW - marginR, y); y += 9;

  // ── Body blocks ──
  blocks.forEach(b => {
    if (!b) return;
    if (b.type === 'spacer') { y += 3; return; }
    if (b.type === 'section') {
      // Heading: Hebrew → variable-regular + teal faux-bold stroke (jsPDF can't embed a real bold
      // Hebrew TTF — it emits only the first glyph, collapsing headings to "ת"); Latin → helvetica bold.
      checkSpace(13); y += 4;
      const stext = String(b.text || '').toUpperCase(); // no-op for Hebrew; uppercase Latin headings
      doc.setFontSize(11); fontFor(stext, 'bold');
      doc.splitTextToSize(stext, contentW).forEach(wl => { checkSpace(6.4); drawLine(wl, textX, y, teal, 'bold', textAlign, HEAD_STROKE); y += 5.4; });
      y += 2; return;
    }
    // Question label — muted grey; Hebrew gets the body faux-bold stroke to match adjacent Latin.
    if (b.type === 'question') { checkSpace(9); drawWrapped(String(b.text || ''), 9, muted, 'normal', 4, BODY_STROKE); y += 0.5; return; }
    if (b.type === 'answer') {
      const skipped = !!b.skipped;
      drawWrapped(skipped ? '—' : String(b.text || ''), 10.5, skipped ? faint : ink, 'normal', 4.7, BODY_STROKE);
      y += 3.5; return;
    }
    // Unknown block → treat as paragraph (ink).
    drawWrapped(String(b.text || ''), 10.5, ink, 'normal', 4.7, BODY_STROKE); y += 2;
  });

  // ── Footer (per page): generation date · Huddledin wordmark · page N / M ──
  const genDate = new Date().toLocaleDateString(rtl ? 'he-IL' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const fy = pageH - 10;
    doc.setDrawColor(rule.r, rule.g, rule.b); doc.setLineWidth(0.2); doc.line(marginL, fy - 4, pageW - marginR, fy - 4);
    doc.setFontSize(8);
    drawLine(genDate, rtl ? (pageW - marginR) : marginL, fy, muted, 'normal', rtl ? 'right' : 'left', BODY_STROKE);
    setColor(teal); doc.setFont(latinFont, 'bold'); doc.text('Huddledin', pageW / 2, fy, { align: 'center' });
    setColor(muted); doc.setFont(latinFont, 'normal');
    const pl = i + ' / ' + totalPages;
    doc.text(pl, rtl ? marginL : (pageW - marginR), fy, { align: rtl ? 'left' : 'right' });
  }

  return doc.output('blob');
}
